import React, { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { FormHelperText } from '@mui/material'
import { Field, FieldProps, Form as FormikForm, Formik } from 'formik'

import { Form, AllergiesAndMedicationsFormProps, LayoutItem } from '../../../ana-toolbox'
import { DiseaseType } from '../../../ana-toolbox'
import { AllergiesField } from '../../components/allergies-field/allergies-field'
import { FormLayout } from '../../components/form-layout/form-layout'
import { SubmitButton } from '../../components/submit-button/submit-button'
import { ToggleMultiCheckbox } from '../../components/toggle-multi-checkbox/toggle-multi-checkbox'
import { Translate } from '../../components/translate/translate'
import { diseasesByType } from '../../utils/config/diseases'
import fields, { validationSchema } from '../../utils/fields/allergiesAndMedications'
import { getFormInitialErrors } from '../../utils/helpers/getFormInitialErrors'
import { LATIN_CHARACTERS_ERROR } from '../../utils/yup/latinValidation'
import { combinedFields } from '../../utils/yup/main'
import { latinTextListSchema } from '../../utils/yup/yup'

const allergiesAndMedicationLayout = ({
  onToggleChange,
  takesOtherMedications,
}: {
  takesOtherMedications: boolean
  onToggleChange: (value: boolean) => void
}): LayoutItem[] => [
  {
    type: 'field',
    field: fields.hasAllergies,
    xs: 12,
  },
  {
    type: 'custom',
    key: 'medicationAllergies',
    xs: 12,
    render: ({ values }) => {
      if (values.hasAllergies !== 'YES') return null

      const hasAllergySelected =
        values?.diseases?.some(
          (disease) => disease.type === DiseaseType.MEDICATION_ALLERGIES || disease.type === DiseaseType.OTHER_ALLERGIES
        ) || false

      return (
        <>
          <AllergiesField
            label="allergies_towards_medication"
            diseaseType={DiseaseType.MEDICATION_ALLERGIES}
            options={diseasesByType.MEDICATION_ALLERGIES}
          />
          {!hasAllergySelected && (
            <FormHelperText error>
              <Translate>allergy_required</Translate>
            </FormHelperText>
          )}
        </>
      )
    },
  },
  {
    type: 'custom',
    key: 'otherAllergies',
    xs: 12,
    render: ({ values }) => {
      if (values.hasAllergies !== 'YES') return null

      const hasAllergySelected =
        values?.diseases?.some(
          (disease) => disease.type === DiseaseType.MEDICATION_ALLERGIES || disease.type === DiseaseType.OTHER_ALLERGIES
        ) || false

      return (
        <>
          <AllergiesField
            label="other_allergies"
            diseaseType={DiseaseType.OTHER_ALLERGIES}
            options={diseasesByType.OTHER_ALLERGIES}
          />

          {!hasAllergySelected && (
            <FormHelperText error>
              <Translate>allergy_required</Translate>
            </FormHelperText>
          )}
        </>
      )
    },
  },
  {
    type: 'field',
    field: combinedFields.allergyPass,
    xs: 12,
  },
  {
    type: 'custom',
    key: 'medications',
    xs: 12,
    render: ({ formHelpers, values }) => {
      const handleValuesChange = (vals: string[]) => {
        formHelpers.setFieldValue('medications', vals)
      }

      const handleToggleChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (!checked) {
          formHelpers.setFieldValue('medications', [])
        }

        onToggleChange(checked)
      }

      return (
        <>
          <ToggleMultiCheckbox
            label="field.do_you_regularly_take_any_other_medication"
            value={takesOtherMedications}
            options={combinedFields.medications.options}
            onSelectedValuesChange={handleValuesChange}
            onToggleChange={handleToggleChange}
            selectedValues={values.medications}
            helperText={combinedFields.medications.helperText}
            allowCustomOptions
            error={latinTextListSchema.isValidSync(values.medications) ? undefined : LATIN_CHARACTERS_ERROR}
          />
        </>
      )
    },
  },
  { type: 'field', field: combinedFields.cigaretteConsumption, xs: 12 },
  {
    type: 'field',
    field: combinedFields.alcoholConsumption,
    xs: 12,
  },
  { type: 'field', field: combinedFields.drugConsumption, xs: 12 },
]

export const AllergiesAndMedicationsForm = ({
  onSubmit,
  initialValues,
  portalAttr,
  loading,
  innerRef,
}: AllergiesAndMedicationsFormProps) => {
  const { t } = useTranslation()
  const [takesOtherMedications, setTakesOtherMedications] = useState(
    initialValues && initialValues.medications ? initialValues.medications.length > 0 : false
  )

  const layout = useMemo(
    () =>
      allergiesAndMedicationLayout({
        takesOtherMedications,
        onToggleChange: setTakesOtherMedications,
      }),
    [takesOtherMedications]
  )

  const submitButton = (
    <Field>
      {(props: FieldProps<unknown, Form>) => {
        const hasAllergy = props.form.values.hasAllergies === 'YES'
        const hasAllergySelected = (props.form.values?.diseases || []).some(
          (disease) => disease.type === DiseaseType.MEDICATION_ALLERGIES || disease.type === DiseaseType.OTHER_ALLERGIES
        )

        return (
          <SubmitButton
            disabled={
              (hasAllergy && !hasAllergySelected) ||
              !props.form.isValid ||
              typeof props.form.values.hasAllergies === 'undefined'
            }
            loading={loading}
            onClick={() => props.form.submitForm()}
          >
            {t('next')}
          </SubmitButton>
        )
      }}
    </Field>
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      innerRef={innerRef}
      validationSchema={validationSchema}
      initialErrors={getFormInitialErrors(validationSchema, initialValues)}
    >
      <FormikForm>
        <FormLayout portalAttr={portalAttr} items={layout} submitButton={submitButton} />
      </FormikForm>
    </Formik>
  )
}
